import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useForm, Controller } from 'react-hook-form';
import {
  Card,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { useParams } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';

import * as actions from '../../redux/actions';
import { BackButton } from '../../components/Button';
import { EmployeeForm } from '../../components/Forms';
import { Loading } from '../../components/Loading';
import { Error } from '../../components/Error';
import { showAlert } from '../../utils/functions/globalDialog';

export default function EditEmployee({ title, subtitle }) {
  const dispatch = useDispatch();
  const { id } = useParams();
  const [employeeImage, setEmployeeImage] = useState([]);
  const [openAddAccount, setOpenAddAccount] = useState(false);
  const [accountForm, setAccountForm] = useState({ username: '', password: '', confirmPassword: '' });
  const [accountInfo, setAccountInfo] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const handleOpenAccountDialog = async () => {
    if (employee?.uid) {
      try {
        const info = await dispatch(actions.userGetByEmployeeId(id));
        setAccountInfo(info);
        setAccountForm({ password: '', confirmPassword: '' });
      } catch (e) {
        setAccountInfo(null);
      }
    } else {
      setAccountInfo(null);
      setAccountForm({ username: '', password: '', confirmPassword: '' });
    }
    setOpenAddAccount(true);
  };
  const employee = useSelector((state) => state.employee);
  const roletype = useSelector((state) => state.roletype);
  const department = useSelector((state) => state.department);
  const role = useSelector((state) => state.role);
  const {
    formState: { errors },
    handleSubmit,
    control,
    watch,
    setValue,
  } = useForm({
    defaultValues: {
      firstname: employee.firstname,
      lastname: employee.lastname,
      role: roletype.id,
      phone_number: employee.phone_number,
    },
  });

  useEffect(() => {
    dispatch(actions.employeeGet(id));
    dispatch(actions.roletypeAll(''));
    dispatch(actions.departmentAll({}));
    dispatch(actions.roleAll());
    return () => { };
  }, []);

  // sync system_role เข้า form เมื่อ employee data โหลดเสร็จ
  useEffect(() => {
    if (employee?.system_role) {
      const ids = (employee.system_role).map((r) => r?.id || r?._id || String(r)).filter(Boolean);
      setValue('system_role', ids);
    }
  }, [employee?.system_role]);

  const onSubmit = async (data) => {
    try {
      const payload = {
        ...data,
        salary: {
          month: data?.salary?.month,
          day: data?.salary?.month / 30,
          hr: data?.salary?.month / 240,
        },
      };

      if (!_.isEmpty(employeeImage)) {
        // eslint-disable-next-line no-param-reassign
        data.image = {
          image: employeeImage[0]?.data_url,
          imageType: 'profile',
          alt: '',
        };
      }

      // console.log('data', data);

      await dispatch(actions.employeePut(id, payload));
      await dispatch(actions.employeeGet(id));
      setEmployeeImage([]);
      showAlert('สำเร็จ');
    } catch (error) {
      console.log(error);
    }
  };
  const handleAddAccount = async () => {
    if (accountForm.password !== accountForm.confirmPassword) {
      showAlert('รหัสผ่านไม่ตรงกัน');
      return;
    }
    try {
      if (employee?.uid) {
        await dispatch(actions.userPut(employee.uid, { password: accountForm.password }));
        showAlert('เปลี่ยนรหัสผ่านสำเร็จ');
      } else {
        await dispatch(actions.userLinkToEmployee(id, {
          username: accountForm.username,
          password: accountForm.password,
        }));
        await dispatch(actions.employeeGet(id));
        showAlert('สร้างบัญชีผู้ใช้สำเร็จ');
      }
      setOpenAddAccount(false);
      setAccountForm({ username: '', password: '', confirmPassword: '' });
    } catch (error) {
      showAlert(error?.response?.data?.error?.message || 'เกิดข้อผิดพลาด', { status: error?.response?.status });
    }
  };

  const renderAddAccountDialog = () => (
    <Dialog open={openAddAccount} onClose={() => setOpenAddAccount(false)} maxWidth="xs" fullWidth>
      {employee?.uid ? (
        <>
          <DialogTitle>บัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3 pt-2">
              <TextField
                label="Username"
                fullWidth
                value={accountInfo?.username || ''}
                InputProps={{ readOnly: true }}
                sx={{ '& .MuiInputBase-root': { backgroundColor: '#f5f5f5' } }}
              />
              <TextField
                label="รหัสผ่านใหม่"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={accountForm.password}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
              />
              <TextField
                label="ยืนยันรหัสผ่านใหม่"
                type={showPassword ? 'text' : 'password'}
                fullWidth
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
              <Button
                size="small"
                onClick={() => setShowPassword((prev) => !prev)}
                sx={{ alignSelf: 'flex-start', textTransform: 'none', p: 0 }}
              >
                {showPassword ? 'ซ่อนรหัสผ่าน' : 'แสดงรหัสผ่าน'}
              </Button>
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddAccount} variant="contained">เปลี่ยนรหัสผ่าน</Button>
            <Button onClick={() => setOpenAddAccount(false)} variant="outlined">ปิด</Button>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle>สร้างบัญชีผู้ใช้</DialogTitle>
          <DialogContent>
            <div className="flex flex-col gap-3 pt-2">
              <TextField
                label="Username"
                fullWidth
                size="small"
                value={accountForm.username}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, username: e.target.value }))}
              />
              <TextField
                label="Password"
                type="password"
                fullWidth
                size="small"
                value={accountForm.password}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, password: e.target.value }))}
              />
              <TextField
                label="ยืนยัน Password"
                type="password"
                fullWidth
                size="small"
                value={accountForm.confirmPassword}
                onChange={(e) => setAccountForm((prev) => ({ ...prev, confirmPassword: e.target.value }))}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleAddAccount} variant="contained">สร้างบัญชี</Button>
            <Button onClick={() => setOpenAddAccount(false)} variant="outlined">ยกเลิก</Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );

  if (employee.isLoading || employee.rows) {
    return <Loading />;
  }
  if (!employee.isLoading && employee.isCompleted) {
    return (
      <div>
        {renderAddAccountDialog()}
        <div className="flex flex-row justify-start pb-4">
          <div>
            <BackButton />
          </div>
        </div>
        <div>
          <Card className="p-6">
            <form onSubmit={handleSubmit(onSubmit)}>
              <EmployeeForm
                control={control}
                Controller={Controller}
                errors={errors}
                employee={employee}
                roletype={roletype}
                role={role}
                employeeImage={employeeImage}
                setEmployeeImage={setEmployeeImage}
                department={department}
                watch={watch}
                setValue={setValue}
              />
              <div className="flex flex-row justify-between gap-1 py-4">
                <div className="flex items-center gap-2">
                  <Button
                    variant="outlined"
                    color="secondary"
                    onClick={handleOpenAccountDialog}
                  >
                    {employee?.uid ? 'จัดการบัญชีผู้ใช้' : 'สร้างบัญชีผู้ใช้'}
                  </Button>
                  <Chip
                    label={employee?.uid ? 'มีบัญชีแล้ว' : 'ยังไม่มีบัญชี'}
                    color={employee?.uid ? 'success' : 'default'}
                    size="small"
                  />
                </div>
                <div className="flex-1" />
                <Button variant="contained" type="submit">
                  บันทึก
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    );
  }
  return <Error />;
}
EditEmployee.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};

EditEmployee.defaultProps = {
  title: '',
  subtitle: '',
};
